// Common Types - Shared across multiple API modules

// API Error types for type-safe error handling
export interface ApiErrorResponse {
  detail?: string;
  non_field_errors?: string[];
  message?: string;
  errors?: Record<string, string[]>;
  details?: {
    fields?: Record<string, string[]>;
  };
  [key: string]: unknown;
}

export interface ApiError extends Error {
  response?: {
    status: number;
    data?: ApiErrorResponse;
  };
  code?: string;
}

// Type guard for API errors
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as ApiError).response === 'object'
  );
}

// Helper to extract error message from API error
export function getApiErrorMessage(error: unknown, defaultMessage = 'An error occurred'): string {
  if (isApiError(error)) {
    const data = error.response?.data;
    if (data?.detail) return data.detail;
    if (data?.non_field_errors?.length) return data.non_field_errors[0];
    // Extract field-level validation errors from standardized response
    const details = data?.details;
    if (details && typeof details === 'object') {
      const fields = details['fields'] as Record<string, string[]> | undefined;
      if (fields && typeof fields === 'object') {
        const firstField = Object.keys(fields)[0];
        if (firstField) {
          const fieldErrors = fields[firstField];
          if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
            return fieldErrors[0];
          }
        }
      }
    }
    if (data?.message) return data.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return defaultMessage;
}

// Base query params for paginated list endpoints
export interface QueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  ordering?: string;
  is_active?: boolean;
  [key: string]: string | number | boolean | undefined;
}

// Approval action response
export interface ActionResponse {
  status: string;
  message: string;
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  is_active: boolean;
  /** Django auth last_login (null if never logged in) */
  last_login?: string | null;
  date_joined?: string;
  last_invitation_sent?: string | null;
  is_staff?: boolean;
  is_superuser?: boolean;
  is_company_admin?: boolean;
  can_manage_users?: boolean;
  has_joined?: boolean;
  /** Admin created the account with a default password; user must change it on first login. */
  password_change_required?: boolean;
  invitation_sent?: boolean;
  // Extended properties for UI
  username?: string;
  role?: string;
  platform?: 'SUPPLIER' | 'CUSTOMER' | 'HELIX';
  companyName?: string;
  company?: number;
  company_type?: 'supplier' | 'customer' | 'helix' | 'logistics';
  profile_id?: number;
  // Legal acceptance tracking
  terms_accepted_version?: number;
  privacy_accepted_version?: number;
  terms_current_version?: number;
  privacy_current_version?: number;
  // Customer-only — null for non-customer users. Used to gate sidebar until approved.
  customer_is_approved?: boolean | null;
  customer_onboarding_status?: 'draft' | 'pending_initial' | 'invited' | 'not_approved' | 'due_diligence' | 'approved' | null;
  // Supplier ERP handler — drives tenant-specific UI (e.g. parts master columns
  // for 'pna', schedule columns for 'dennis_eagle'). Null for non-supplier users
  // and for suppliers without an import profile.
  import_handler?: 'pna' | 'dennis_eagle' | 'default' | null;
}

export interface Company {
  id: number;
  name: string;
  company_type: 'supplier' | 'customer' | 'helix';
  group?: number;
  group_name?: string;
  primary_email: string;
  primary_phone?: string;
  website?: string;
  description?: string;
  // Address fields
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  latitude?: string | null;
  longitude?: string | null;
  // Additional fields
  registration_number?: string;
  tax_id?: string;
  customer_code?: string;
  site_count: number;
  user_count: number;
  is_active: boolean;
  created_at: string;
  // CR-3396: Portal mode for dual-role companies
  portal_mode?: 'supplier' | 'customer' | 'both';
  /** Poeton-style intra-site customer view toggle */
  supports_dual_portal?: boolean;
  // Supplier Portal Mode: Enable customers to manage their suppliers
  supplier_portal_enabled?: boolean;
}

export interface Site {
  id: number;
  company: number;
  company_name: string;
  name: string;
  code: string;
  site_type: string;
  city: string;
  country: string;
  warehouse_count: number;
  is_active: boolean;
  group_id?: number | null;
  group_name?: string | null;
}

export interface Warehouse {
  id: number;
  site: number;
  site_name: string;
  name: string;
  code: string;
  warehouse_type: string;
  address: string;
  city: string;
  country: string;
  location_count: number;
  is_active: boolean;
}

export interface Location {
  id: number;
  warehouse: number;
  warehouse_name: string;
  site_name: string;
  company_name: string;
  name: string;
  code: string;
  location_type: string;
  aisle: string;
  shelf: string;
  bin_number: string;
  capacity?: number;
  max_weight?: string;
  description: string;
  full_path: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CompanyGroup {
  id: number;
  name: string;
  description: string;
  headquarters_country: string;
  website: string;
  parent_group?: number;
  parent_group_name?: string;
  level: number;
  company_count: number;
  child_group_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** CR-4579: Response from /company-groups/:id/members/ endpoint */
export interface CompanyGroupMembersResponse {
  group: CompanyGroup;
  companies: CompanyListItem[];
  stats: {
    total_companies: number;
    total_orders: number;
    total_order_value: string;
    /** Platform base currency (GBP) — convert with formatCurrencyFromBase on the frontend. */
    total_order_value_currency?: string;
    total_shipments: number;
  };
}

/** Minimal company fields returned by CompanyListSerializer */
export interface CompanyListItem {
  id: number;
  name: string;
  company_type: string;
  portal_mode?: 'supplier' | 'customer' | 'both';
  supports_dual_portal?: boolean;
  group: number | null;
  group_name: string | null;
  primary_email: string;
  city: string;
  country: string;
  site_count: number;
  user_count: number;
  is_active: boolean;
  created_at: string;
}

// Hierarchy tree node types - matches backend API response
export interface HierarchyNode {
  id: string; // Prefixed ID like "group_1", "company_2"
  entity_id: number; // Actual database ID
  type: 'group' | 'division' | 'company' | 'warehouse' | 'location';
  name: string;
  is_active: boolean;
  children: HierarchyNode[];
  // Type-specific fields
  code?: string;
  site_type?: string;
  warehouse_type?: string;
  location_type?: string;
  company_type?: string;
  level?: number;
  description?: string;
  metadata?: {
    city?: string;
    country?: string;
    headquarters_country?: string;
    website?: string;
    primary_email?: string;
    manager_name?: string;
  };
}

export interface HierarchyTreeResponse {
  tree: HierarchyNode[];
  stats: {
    total_groups: number;
    total_divisions: number;
    total_companies: number;
    total_warehouses: number;
    total_locations: number;
  };
  labels?: {
    group: string;
    division: string;
    site: string;
  };
}

export interface Role {
  id: number;
  name: string;
  description: string;
  company_type: 'helix' | 'supplier' | 'customer';
  department: 'admin' | 'purchasing' | 'quality' | 'finance' | 'operations' | 'logistics' | 'engineering' | 'custom';
  department_display: string;
  is_admin_role: boolean;
  // General permissions
  can_manage_users: boolean;
  can_manage_sites: boolean;
  can_view_all_sites: boolean;
  can_manage_customers: boolean;
  can_manage_suppliers: boolean;
  // Purchasing permissions
  can_manage_orders: boolean;
  can_approve_orders: boolean;
  can_view_pricing: boolean;
  // Quality permissions
  can_manage_quality: boolean;
  can_create_ncr: boolean;
  can_approve_ncr: boolean;
  // Finance permissions
  can_view_financials: boolean;
  can_manage_invoices: boolean;
  // Reporting
  can_view_reports: boolean;
  can_export_data: boolean;
  permissions_summary: string;
  user_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserProfile {
  id: number;
  user: User;
  company: number;
  company_name: string;
  company_type: 'supplier' | 'customer' | 'helix';
  role: number;
  role_name: string;
  role_details?: Role;
  job_title?: string;
  department?: string;
  phone?: string;
  sites: number[];
  site_names: string[];
  is_approved: boolean;
  /** Master notification switches (admin-managed). Default OFF (opt-in). */
  notif_in_app_enabled?: boolean;
  notif_email_enabled?: boolean;
  created_at: string;
  updated_at?: string;
}

export interface UserManagementLog {
  id: number;
  performed_by_email: string;
  performed_by_name: string;
  target_user_email: string;
  target_user_name: string;
  company_name: string;
  action: string;
  action_display: string;
  description: string;
  old_value: Record<string, unknown>;
  new_value: Record<string, unknown>;
  ip_address?: string;
  timestamp: string;
}

export interface UserSitePermission {
  id: number | null;
  user_profile: number;
  site: number;
  site_name: string;
  site_code: string;
  user_email: string;
  is_linked: boolean;
  is_active: boolean;
  can_purchasing: boolean;
  can_quality: boolean;
  can_finance: boolean;
  is_admin: boolean;
  can_approve: boolean;
  is_read_only: boolean;
  notify_procurement: boolean;
  notify_quality: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface DashboardStats {
  total_companies: number;
  total_suppliers: number;
  total_customers: number;
  total_sites: number;
  total_users: number;
  pending_approvals: number;
  active_relationships: number;
  recent_registrations: number;
}

export interface CompanyRegistrationData {
  name: string;
  company_type: 'supplier' | 'customer';
  description?: string;
  website?: string;
  primary_email: string;
  primary_phone?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  user_email: string;
  user_first_name: string;
  user_last_name: string;
  user_password: string;
  user_password_confirm: string;
}

// Impersonation Types
export interface ImpersonatableUser {
  user_id: number;
  email: string;
  full_name: string;
  first_name: string;
  last_name: string;
  job_title?: string;
  role?: string;
  is_active: boolean;
}

export interface ImpersonatableCompany {
  company_id: number;
  company_name: string;
  company_type: 'supplier' | 'customer';
  users: ImpersonatableUser[];
}

export interface ImpersonateResponse {
  message: string;
  impersonating: boolean;
  original_user: User;
  original_token: string;
  target_user: User;
  target_token: string;
  token: string;
}

export interface StopImpersonateResponse {
  message: string;
  impersonating: boolean;
  user: User;
  token: string;
}

// View Context types (for database-backed view switching)
export interface ViewContext {
  current_view_level: 'GROUP' | 'COMPANY' | 'SITE';
  current_group: number | null;
  current_group_name: string | null;
  current_company: number | null;
  current_company_name: string | null;
  current_site: number | null;
  current_site_name: string | null;
  portal_mode: 'supplier' | 'customer' | 'supplier_manager';
  has_site_restrictions: boolean;
  platform_role: 'supplier' | 'customer';
  enabled_modules: string[] | null;  // null = all modules enabled
  supports_dual_portal?: boolean;
}

export interface ViewContextUpdate {
  current_view_level?: 'GROUP' | 'COMPANY' | 'SITE';
  current_group?: number | null;
  current_company?: number | null;
  current_site?: number | null;
  portal_mode?: 'supplier' | 'customer' | 'supplier_manager';
}

// Dashboard Tab Visibility (CR-4441)
export interface DashboardTabsVisibility {
  'default': boolean;
  'group-kpi': boolean;
  'group-detail-kpi': boolean;
  'site-kpi': boolean;
  'cost': boolean;
  'emissions': boolean;
  'compliance': boolean;
  'future-forecast': boolean;
  'date-adherence': boolean;
  'expediting': boolean;
  'performance-ratings': boolean;
  [key: string]: boolean;
}

// Customer Card Field Visibility (approved customer directory cards)
export interface CustomerCardFieldsVisibility {
  logo: boolean;
  risk_badge: boolean;
  otd: boolean;
  rating: boolean;
  questionnaire_status: boolean;
  [key: string]: boolean;
}

// Customer Profile page layout (per-tenant). Controls which stat cards,
// tabs and overview widgets appear on /customers/:id.
export interface CustomerProfileLayout {
  stats: { otd: boolean; total_orders: boolean; [key: string]: boolean };
  tabs: {
    performance: boolean; orders: boolean; contracts: boolean;
    quality: boolean; questionnaires: boolean; credit_scores: boolean;
    users: boolean; activity_log: boolean; group_structure: boolean;
    [key: string]: boolean;
  };
  overview: { performance_summary: boolean; capabilities: boolean; [key: string]: boolean };
}

// Help & Support page layout (per-tenant, superuser-editable). Used to hide
// FAQ categories and sidebar widgets that are irrelevant for a given tenant
// (e.g. MAB Compliance hides Purchasing because they don't run POs).
export interface HelpLayout {
  faq: {
    getting_started: boolean;
    purchasing: boolean;
    customer_management: boolean;
    documents_communication: boolean;
    [key: string]: boolean;
  };
  sidebar: {
    tour: boolean;
    contact_support: boolean;
    system_status: boolean;
    [key: string]: boolean;
  };
}

// Data Import Tile Visibility (individual upload tiles on /data-import)
export interface DataImportTilesVisibility {
  commodity: boolean;
  faultcodes: boolean;
  departments: boolean;
  warehouses: boolean;
  customers: boolean;
  customer_additional: boolean;
  grns: boolean;
  pos: boolean;
  ncrs: boolean;
  forecasts: boolean;
  users: boolean;
  [key: string]: boolean;
}

// Purchasing Tab Visibility
export interface PurchasingTabsVisibility {
  rfq: boolean;
  pricing: boolean;
  po_headers: boolean;
  po_lines: boolean;
  forecast: boolean;
  asn: boolean;
  deliveries: boolean;
  call_off: boolean;
  [key: string]: boolean;
}

// Quality Tab Visibility
export interface QualityTabsVisibility {
  ncrs: boolean;
  concessions: boolean;
  approval_register: boolean;
  [key: string]: boolean;
}

// Open Orders Visibility (CR-4422) - ON = hidden in Open Orders view
export interface OpenOrdersVisibility {
  hide_exceptions: boolean;
  hide_decline_button: boolean;
  hide_propose_qty_ordered: boolean;
  hide_actions: boolean;
  hide_trend_line: boolean;
  hide_otif_override_button: boolean;
  hide_forecasting_from_customer: boolean;
  hide_communication_box: boolean;
  [key: string]: boolean;
}

// Menu Visibility
export interface MenuVisibility {
  // Supplier Menu Items
  dashboard: boolean;
  customerPerformance: boolean;
  sitePerformance: boolean;
  exchangeRates: boolean;
  calendar: boolean;
  purchasing: boolean;
  rfq: boolean;
  quality: boolean;
  concessions: boolean;
  customers: boolean;
  documents: boolean;
  customerIntelligence: boolean;
  customerOnboarding: boolean;
  userInvitations: boolean;
  userActivityHistory: boolean;
  questionnaire: boolean;
  newsRelease: boolean;
  adminSetup: boolean;
  partsMaster: boolean;
  /** Isolated Part Schedule module — per-install opt-in via Admin Setup.
   *  Backend additionally gates by SupplierImportProfile.part_schedule_module_enabled. */
  partSchedule: boolean;
  /** Analytics / Reports sidebar item (supplier + customer share the same key). */
  analytics: boolean;
  dataImport: boolean;
  // Customer Menu Items
  purchaseOrders: boolean;
  companyProfile: boolean;
  suppliers: boolean;  // Suppliers list for customer portal
  callOff: boolean;    // Call Off tab in Purchasing
  // Customer-side navigation toggles — separate from the supplier-side
  // keys so a tenant can keep e.g. News Release on for supplier users
  // but switched off for customer users, or hide Dashboard on the
  // customer sidebar without affecting the supplier one.
  customerDashboard: boolean;
  customerQuestionnaires: boolean;
  customerCalendar: boolean;
  customerSuppliers: boolean;
  customerDocuments: boolean;
  customerNewsRelease: boolean;
  // Allow additional boolean properties for backwards compatibility
  [key: string]: boolean;
}

// Platform Settings
export interface PlatformSettings {
  id: number;
  // Branding & Theme
  company_name: string;
  default_theme: number;
  available_themes: string[];
  // Colors
  background_color: string;
  login_color: string;
  font_color: string;
  button_color: string;
  // Contact Information
  website: string;
  support_email: string;
  sales_email: string;
  no_reply_email: string;
  // Email Settings
  email_subject: string;
  email_signature: string;
  // Platform Configuration
  main_menu_type: string;
  free_mode: boolean;
  main_company_can_login_as: boolean;
  menu_visibility: MenuVisibility;
  // Feature Toggles
  automation_related: boolean;
  use_disposition_in_ncr: boolean;
  warehouse_module: boolean;
  turn_email_mab: boolean;
  switch_part_repository: boolean;
  switch_call_off_tab: boolean;
  quality_measure_ppk: boolean;
  // NCR Template
  ncr_template: 1 | 2;
  // Barratt Feature Flags
  feature_flags?: {
    delivery_appeals?: boolean;
    spend_leakage?: boolean;
    bulk_upload?: boolean;
    commodity_codes?: boolean;
    customer_stock_levels?: boolean;
    allow_customer_decline?: boolean;
    po_line_schedules?: boolean;
    job_documents?: boolean;
    show_forecast_total?: boolean;
    portal_mode?: 'standard' | 'compliance' | 'supplier_portal';
  };
  // IP Restriction
  restrict_country: string;
  restrict_country_enabled: boolean;
  // Upload Limits (Feature 5)
  max_upload_size_mb: number;
  allowed_file_types: string[];
  // Document Versioning (Feature 8)
  auto_version_documents: boolean;
  version_format: 'numeric' | 'semantic';
  retain_old_versions: boolean;
  // Order-progress notification matrix + cadence (freeform JSON; typed as
  // NotificationConfig in the admin settings layer).
  default_notification_preferences: Record<string, unknown>;
  system_alert_emails: string;
  emails_paused: boolean;
  email_rate_limit_per_day: number;
  notify_joined_users_only: boolean;
  // Customer Onboarding
  customer_onboarding_enabled: boolean;
  // Dashboard Tab Visibility (CR-4441)
  dashboard_tabs: DashboardTabsVisibility;
  customer_dashboard_tabs: DashboardTabsVisibility;
  purchasing_tabs: PurchasingTabsVisibility;
  quality_tabs: QualityTabsVisibility;
  customer_card_fields: CustomerCardFieldsVisibility;
  data_import_tiles: DataImportTilesVisibility;
  customer_default_landing_path: string;
  // CR-5263 OTD calculation rules
  otd_hold_suspends_promise_change_impact: boolean;
  otd_promise_change_off_hold_counts: boolean;
  otd_allow_line_level_override: boolean;
  otd_apply_retroactively: boolean;
  otd_grace_days: number;
  otd_include_on_hold: boolean;
  // Tracking PRO — live courier tracking on ASNs
  tracking_pro_enabled: boolean;
  tracking_pro_update_asn_status: boolean;
  /** Courier code → provider carrier slug overrides, e.g. `{ dpd: 'dpd-uk' }`. */
  tracking_pro_courier_slugs: Record<string, string>;
  /** Read-only: whether the provider API key is present in the environment. */
  tracking_pro_configured: boolean;
  /** Read-only: built-in courier → slug defaults, shown as placeholders. */
  tracking_pro_courier_slug_defaults: Record<string, string>;
  // NCR Leadtime Settings
  containment_action_leadtime: number;
  full_corrective_action_leadtime: number;
  // Approval Expiry Alert
  approval_expiry_alert_days: number;
  // NCR Email Notification Settings
  ncr_supplier_department: string;
  ncr_customer_department: string;
  ncr_email_frequency: 'weekly' | 'weekdays';
  ncr_email_weekdays: number[];
  // Purchasing Email Notification Settings (CR-4421)
  purchasing_supplier_department: string;
  purchasing_customer_department: string;
  purchasing_email_frequency: 'weekly' | 'weekdays';
  purchasing_email_weekdays: number[];
  purchasing_arrears_report_customer: boolean;
  purchasing_arrears_report_supplier: boolean;
  // Auto Approve Delivery Appeal (CR-4423)
  auto_approve_delivery_appeal: boolean;
  // Open Orders Visibility (CR-4422)
  open_orders_visibility: OpenOrdersVisibility;
  // Cost of Quality
  ncr_cost_of_quality: number;
  // Hierarchy Labels
  hierarchy_level_1_label: string;
  hierarchy_level_2_label: string;
  hierarchy_level_3_label: string;
  // Legal Content
  terms_content: string;
  privacy_content: string;
  terms_version: number;
  privacy_version: number;
  terms_updated_at: string | null;
  privacy_updated_at: string | null;
  // Metadata
  updated_at: string;
}

export interface LegalContent {
  terms_content: string;
  privacy_content: string;
  terms_version: number;
  privacy_version: number;
  terms_updated_at: string | null;
  privacy_updated_at: string | null;
}

export interface LegalAcceptRequest {
  terms_version?: number;
  privacy_version?: number;
}

export interface LegalAcceptResponse {
  message: string;
  terms_accepted_version: number;
  privacy_accepted_version: number;
}

export interface PlatformSettingsUpdate {
  company_name?: string;
  default_theme?: number;
  available_themes?: string[];
  background_color?: string;
  login_color?: string;
  font_color?: string;
  button_color?: string;
  website?: string;
  support_email?: string;
  sales_email?: string;
  no_reply_email?: string;
  email_subject?: string;
  email_signature?: string;
  main_menu_type?: string;
  free_mode?: boolean;
  main_company_can_login_as?: boolean;
  menu_visibility?: Partial<MenuVisibility>;
  automation_related?: boolean;
  use_disposition_in_ncr?: boolean;
  warehouse_module?: boolean;
  turn_email_mab?: boolean;
  switch_part_repository?: boolean;
  switch_call_off_tab?: boolean;
  quality_measure_ppk?: boolean;
  // Barratt Feature Flags
  feature_flags?: {
    delivery_appeals?: boolean;
    spend_leakage?: boolean;
    bulk_upload?: boolean;
    commodity_codes?: boolean;
    customer_stock_levels?: boolean;
    allow_customer_decline?: boolean;
    po_line_schedules?: boolean;
    job_documents?: boolean;
    show_forecast_total?: boolean;
    portal_mode?: 'standard' | 'compliance' | 'supplier_portal';
  };
  restrict_country?: string;
  restrict_country_enabled?: boolean;
  // Upload Limits
  max_upload_size_mb?: number;
  allowed_file_types?: string[];
  // Document Versioning
  auto_version_documents?: boolean;
  version_format?: 'numeric' | 'semantic';
  retain_old_versions?: boolean;
  // Notification Defaults
  default_notification_preferences?: Record<string, unknown>;
  system_alert_emails?: string;
  emails_paused?: boolean;
  email_rate_limit_per_day?: number;
  notify_joined_users_only?: boolean;
  // Customer Onboarding
  customer_onboarding_enabled?: boolean;
  // Dashboard Tab Visibility (CR-4441)
  dashboard_tabs?: Partial<DashboardTabsVisibility>;
  customer_dashboard_tabs?: Partial<DashboardTabsVisibility>;
  purchasing_tabs?: Partial<PurchasingTabsVisibility>;
  quality_tabs?: Partial<QualityTabsVisibility>;
  customer_card_fields?: Partial<CustomerCardFieldsVisibility>;
  data_import_tiles?: Partial<DataImportTilesVisibility>;
  customer_default_landing_path?: string;
  // CR-5263 OTD calculation rules
  otd_hold_suspends_promise_change_impact?: boolean;
  otd_promise_change_off_hold_counts?: boolean;
  otd_allow_line_level_override?: boolean;
  otd_apply_retroactively?: boolean;
  otd_grace_days?: number;
  otd_include_on_hold?: boolean;
  // Tracking PRO — live courier tracking on ASNs
  tracking_pro_enabled?: boolean;
  tracking_pro_update_asn_status?: boolean;
  tracking_pro_courier_slugs?: Record<string, string>;
  // NCR Leadtime Settings
  containment_action_leadtime?: number;
  full_corrective_action_leadtime?: number;
  // Approval Expiry Alert
  approval_expiry_alert_days?: number;
  // NCR Email Notification Settings
  ncr_supplier_department?: string;
  ncr_customer_department?: string;
  ncr_email_frequency?: 'weekly' | 'weekdays';
  ncr_email_weekdays?: number[];
  // Purchasing Email Notification Settings (CR-4421)
  purchasing_supplier_department?: string;
  purchasing_customer_department?: string;
  purchasing_email_frequency?: 'weekly' | 'weekdays';
  purchasing_email_weekdays?: number[];
  purchasing_arrears_report_customer?: boolean;
  purchasing_arrears_report_supplier?: boolean;
  // Auto Approve Delivery Appeal (CR-4423)
  auto_approve_delivery_appeal?: boolean;
  // Open Orders Visibility (CR-4422)
  open_orders_visibility?: Partial<OpenOrdersVisibility>;
  // Cost of Quality
  ncr_cost_of_quality?: number;
  // Hierarchy Labels
  hierarchy_level_1_label?: string;
  hierarchy_level_2_label?: string;
  hierarchy_level_3_label?: string;
  // Legal Content
  terms_content?: string;
  privacy_content?: string;
  terms_version?: number;
  privacy_version?: number;
  terms_updated_at?: string | null;
  privacy_updated_at?: string | null;
}

// Country Detection Response (Feature 7)
export interface CountryDetectionResponse {
  ip: string;
  country: string;
  country_code: string;
  message?: string;
}

// Merge Companies Response (Feature 1)
export interface MergeCompaniesResponse {
  message: string;
  target_company: { id: number; name: string };
  source_company_deleted: string;
  transfers: Array<{ model: string; field: string; records_transferred: number }>;
  total_records_transferred: number;
}

// Date Adjustment (Feature 6)
export interface DateAdjustmentResponse {
  message: string;
  direction: 'plus' | 'minus';
  total_rows_affected: number;
  details: Array<{ table: string; column: string; rows_affected: number }>;
}

export interface DateAdjustmentLog {
  id: number;
  direction: string;
  direction_display: string;
  affected_tables: Array<{ table: string; column: string; rows_affected: number }>;
  total_rows: number;
  performed_by: string;
  performed_at: string;
  notes: string;
}

// Compliance Certification (Feature 4)
export interface ComplianceCertification {
  id: number;
  company: number;
  company_name: string;
  certification_type: string;
  certification_type_display: string;
  name: string;
  document: string | null;
  expiry_date: string;
  issued_by: string;
  notes: string;
  status: 'valid' | 'expiring_soon' | 'expired';
  days_until_expiry: number;
  created_by: number | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

// User Delegation (Feature 2)
export interface UserDelegation {
  id: number;
  user: number;
  user_name: string;
  user_email: string;
  delegate_user: number;
  delegate_user_name: string;
  delegate_user_email: string;
  company: number;
  company_name: string;
  start_date: string;
  end_date: string;
  reason: string;
  is_active: boolean;
  is_currently_active: boolean;
  created_at: string;
  updated_at: string;
}

// Public branding response type
export interface PublicBranding {
  company_name: string;
  login_title?: string;
  login_color: string;
  button_color: string;
  background_color: string;
  font_color: string;
  company_logo?: string;
  company_dark_logo?: string;
}

/**
 * Per-site email + notification overrides.
 *
 * Every override is nullable and `null` means **inherit the platform value**,
 * never "off" — so a site listed here with all-null fields behaves exactly as
 * one with no row at all. `id` is null for a site that has no stored row yet.
 */
export interface SiteEmailSettings {
  id: number | null;
  site: number;
  site_name: string;
  emails_paused: boolean | null;
  rate_limit_per_day: number | null;
  notify_joined_users_only: boolean | null;
  notification_config: Record<string, unknown> | null;
  created_at: string | null;
  updated_at: string | null;
}

/** Writable subset of SiteEmailSettings. Send null to clear an override. */
export interface SiteEmailSettingsUpdate {
  emails_paused?: boolean | null;
  rate_limit_per_day?: number | null;
  notify_joined_users_only?: boolean | null;
  notification_config?: Record<string, unknown> | null;
}

/**
 * One customer's override of the per-recipient daily email cap.
 *
 * `email_rate_limit_per_day` null means the customer uses the platform
 * default; a number is a custom cap. Clearing an override is a PATCH with
 * null, not a delete.
 */
export interface CustomerEmailLimit {
  id: number;
  company_name: string;
  email_rate_limit_per_day: number | null;
}

/** A single stored exchange rate: 1 GBP = `rate` of `currency` on `rate_date`. */
export interface ExchangeRateRow {
  id: number;
  currency: string;
  rate: string;
  rate_date: string;
  source: string;
  fetched_at?: string;
}

/** Month x currency matrix from /core/exchange-rates/monthly/. */
export interface MonthlyExchangeRates {
  base_currency: string;
  /** Newest month first, as "YYYY-MM". */
  months: string[];
  /** Currency codes present, excluding the base. */
  currencies: string[];
  rates: Record<string, Record<string, { rate: string; rate_date: string }>>;
}
